import { t } from "i18next";
import { makeAutoObservable } from "mobx";
import { BreadcrumbItem } from "src/appStore";
import { mdmBreadcrumbs } from "src/common/mdmBreadcrumbs";
import { onError } from "src/common/onError";
import { RemoteData } from "src/common/RemoteData";
import { msgQueueStore } from "src/components/MsgQueue/msgQueueStore";
import { RefSelectStore } from "src/components/RefSelect/RefSelectStore";
import { RoleFilters, ZRole } from "src/types";
import { ZUser } from "src/types/ZUser";

import {
  createUser,
  loadUser,
  updateUser,
} from "../../../../../business/user/apiUsers";
import { UserFormData } from "../types";
import { makeRolesRefStore } from "./makeRolesRefStore";
import { userFormData2UserSaveData } from "./transform";

export class SingleUserStore {
  data: RemoteData<ZUser | null> = { status: "none" };

  setData(newData: RemoteData<ZUser | null>) {
    this.data = newData as RemoteData<ZUser | null>;
  }

  loading = false;

  setLoading(loading: boolean) {
    this.loading = loading;
  }

  rolesRefStore: RefSelectStore<ZRole, RoleFilters> =
    makeRolesRefStore(undefined);

  setRolesRefStore(refSelectStore: RefSelectStore<ZRole, RoleFilters>) {
    this.rolesRefStore = refSelectStore;
  }

  get crumbs(): BreadcrumbItem[] {
    const { data } = this;

    let name = "...";
    if (data.status === "ready") {
      name = data?.result?.login ?? "Новый пользователь";
    }
    return mdmBreadcrumbs([
      { type: "UsersAndRoles", disabled: true },
      { type: "AdminUsers" },
      { type: "Name", name },
    ]);
  }

  constructor() {
    makeAutoObservable(this);
  }

  get userId() {
    if (this.data.status !== "ready" || this.data.result === null) {
      return undefined;
    }
    return this.data.result.id;
  }

  loadUser = async (userId: string | undefined) => {
    if (!userId) {
      this.setData({ status: "ready", result: null });
      return;
    }
    this.setData({ status: "wait" });
    try {
      const user = await loadUser(userId);
      this.setData({ status: "ready", result: user });
      this.setRolesRefStore(makeRolesRefStore(user));
    } catch (error) {
      this.setData({ status: "error", error });
    }
  };

  saveUser = async (userData: UserFormData): Promise<ZUser | null> => {
    const { userId } = this;
    const isNew = !userId;

    const previousActive =
      this.data.status === "ready" ? this.data.result?.active : undefined;

    this.setLoading(true);
    try {
      const data4save = userFormData2UserSaveData(userData);
      const result = isNew
        ? await createUser(data4save)
        : await updateUser(userId || "", data4save);
      this.setData({ status: "ready", result });
      const msg = isNew
        ? t("Created successfully [entity]", { entity: t("User") })
        : t("Updated successfully [entity]", { entity: t("User") });
      msgQueueStore.add({
        type: "success",
        message: msg,
      });

      const isDeactivated = !isNew && previousActive && !result.active;
      if (isDeactivated) {
        msgQueueStore.add({
          type: "success",
          message: `Пользователь «${result.login}» деактивирован`,
          description: "Доступ будет отозван в течение 1 минуты",
        });
      }
      return result;
    } catch (error) {
      onError(error);
      return null;
    } finally {
      this.setLoading(false);
    }
  };

  /**
   * сейчас singleUserStore используется как глобальный store
   * для всех страниц пользователей
   * поэтому нужно очищать данные при unmount компонента
   */
  clear() {
    this.setData({ status: "none" });
    this.setRolesRefStore(makeRolesRefStore(undefined));
  }
}

export const singleUserStore = new SingleUserStore();
